import React, {useCallback, useEffect, useMemo} from "react";
import {Stepper} from "@stepperize/react";
import {Accordion} from "radix-ui";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronRight} from "@fortawesome/pro-solid-svg-icons";
import {CreateProjectStep} from "../stepper/create-project-steps.ts";
import Button from "@/components/buttons/button";
import useFilters from "@/hooks/use-filters.ts";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import useProjectCreationContext from "../context/use-project-creation-context.ts";
import useProjectsContext from "@/services/context/projects/use-projects-context.ts";
import Input from "@/components/input";
import RadioButton from "@/components/radio-button";
import FlowMode from "@/models/enums/flow-mode.ts";
import { validateFileName, getInvalidFileNameMessage } from "@/utils/text/is-valid-file-name.ts";

interface ProjectInformationsStepProps {
    stepper: Stepper<CreateProjectStep[]>
}

const ProjectInformationsStep: React.FC<ProjectInformationsStepProps> = ({stepper}) => {

    const { next } = stepper;

    const { projects } = useProjectsContext();
    const { isLoading, data: filtersData } = useFilters();
    const { data, flowModeCreationProject, setFlowModeCreationProject, setProjectInformations, createProject } = useProjectCreationContext();

    const [projectName, setProjectName] = React.useState<string>(data.name);
    const [trimmedName, setTrimmedName] = React.useState<string>(projectName.trim());
    const [localisationId, setLocalisationId] = React.useState<number>(data.localisationId);
    
    const [isValid, setIsValid] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    
    useEffect(() => {
        setTrimmedName(projectName.trim());
    }, [projectName]);

    const localisationsByLevel = useMemo(() => {
        if(filtersData == null)
            return null;

        return (
            [...filtersData]
                .sort((a, b) => a.levelId - b.levelId)
                .map((level) => ({
                    ...level,
                    localisations: [...level.localisations].sort((a, b) => a.name.localeCompare(b.name)),
                }))
        );
    }, [filtersData]);

    const handleClickContinue = useCallback(() => {
        const creationModel = setProjectInformations(trimmedName, localisationId);
        if(flowModeCreationProject === FlowMode._3D2D) {
            createProject(creationModel);
        }

        next();

    }, [createProject, flowModeCreationProject, localisationId, next, trimmedName, setProjectInformations]);

    useEffect(() => {
        if(projects == null) return;
        
        const validation = validateFileName(projectName);
        if(!validation.isValid) {
            setErrorMessage(getInvalidFileNameMessage(validation.reason));
            setIsValid(false);
        } else {
            const exists = projects.some((project) => (project.name.localeCompare(trimmedName, undefined, { sensitivity: "accent" }) === 0));
            setErrorMessage(exists ? 'Un project avec ce nom existe déjà' : null);
            setIsValid(!exists);
        }
 
    }, [projectName, projects, trimmedName]);

    if(isLoading)
        return <SimpleLoadingIndicator />
    
    const isDisabled = trimmedName === "" || localisationId === -1 || !isValid;

    return (
        <div className="flex flex-col flex-1 h-full min-h-0 justify-between">
            <div className="grid min-h-0 flex-1 grid-cols-[auto_1fr] grid-rows-[auto_auto_minmax(0,1fr)] gap-y-3 gap-x-4 overflow-hidden text-sm font-medium text-gray-900 p-1">
                <span className='self-center'>Nom du projet</span>
                <Input type="text" value={projectName} className='font-normal'
                       onChange={(e) => setProjectName(e.target.value)}/>

                {projectName !== "" && !isValid && <div className='text-red-500 col-span-2 text-end'>{errorMessage}</div>}

                <span>Localisation</span>
                <Accordion.Root type="multiple" className="flex flex-col gap-2 min-h-0 h-full overflow-auto" defaultValue={localisationsByLevel?.flatMap(l => l.levelName)}>
                    {localisationsByLevel?.map((level) => (
                        <Accordion.Item
                            key={level.levelId}
                            value={level.levelName}>
                            <Accordion.Header className='bg-gray-50 rounded px-2'>
                                <Accordion.Trigger className='group w-full'>
                                    <div className='flex flex-row items-center gap-x-4'>
                                        <FontAwesomeIcon icon={faChevronRight}
                                                         className='transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90 text-xs mb-px'/>

                                        <span className='font-semibold uppercase'>{level.levelName}</span>
                                    </div>
                                </Accordion.Trigger>
                            </Accordion.Header>

                            <Accordion.Content className="ml-7 mt-2 border-l">
                                <div className="flex flex-col gap-1 mx-1">
                                    {level.localisations.map((localisation) => (
                                        <div className='flex flex-row gap-x-4 items-center hover:bg-gray-100 pl-4 py-0.5 rounded-sm' key={localisation.id}
                                             onClick={() => setLocalisationId(localisation.id)}>
                                            <RadioButton name='localisation'
                                                         checked={localisation.id === localisationId} 
                                                         onChange={() => setLocalisationId(localisation.id)}/>
                                            <span className='font-normal select-none' >{localisation.name}</span>
                                        </div>

                                    ))}
                                </div>
                            </Accordion.Content>
                        </Accordion.Item>
                    ))}
                </Accordion.Root>
            </div>

            <div className="mt-2 flex shrink-0 justify-between">
                <Button onClick={() => setFlowModeCreationProject(undefined)}>Revenir en arrière</Button>
                <Button disabled={isDisabled} onClick={handleClickContinue}>
                    Continuer
                </Button>
            </div>
        </div>
    )
}


export default ProjectInformationsStep;